import { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import cutileiApi from 'services/cutileiApi'
import { ONLINE_PAYMENT_ENABLED } from 'services/features'
import { logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import Address from 'services/Address'
import ServicesForm from './ServicesForm'
import CategoriesForm from './CategoriesForm'
import TimeTablesForm from './TimeTablesForm'
import ProfessionalsForm from './ProfessionalsForm'
import Confirmation from './Confirmation'
import Payment from './Payment'
import { IoLocationSharp } from 'react-icons/io5'
import CutileiLogo from 'icons/logo-cutilei-simple.png'
import * as FA from 'react-icons/fa'

import {
  Container,
  Column,
  Image,
  SmallImage,
  Form,
  Row,
  LogoRow,
  TextRow,
  StepRow,
  StepContainer,
  StepCircle,
  StepLabel,
  StepLine,
  // RatingContainer,
  Title,
  SubTitle,
  InfoText,
  LinkText,
  Block,
  Circle,
  SmallButton
} from './styles'

/*
  Quando o salão exige sinal, é o pagamento que cria o agendamento — então este é o último
  passo, e o único de onde não se volta: voltar remontaria o formulário de confirmação e
  cadastraria o cliente de novo.
*/
const PAYMENT_STEP = 5

export default function ScheduleLink () {
  const location = useLocation ()
  const navigate = useNavigate ()
  const { business_id, schedule_id } = useParams ()
  const customer = location.state?.customer

  const [loading, setLoading] = useState (true)
  const [business, setBusiness] = useState (null)
  const [services, setServices] = useState ([])
  const [serviceCategories, setServiceCategories] = useState ([])
  const [schedule, setSchedule] = useState ({})
  const [alternativeSchedules, setAlternativeSchedules] = useState ([])
  const [onlinePayment, setOnlinePayment] = useState (null)
  const [createdSchedules, setCreatedSchedules] = useState ([])

  /*
    O que o cliente pretende marcar, enquanto ele paga. Só existe quando o salão exige sinal:
    aí o agendamento ainda não foi criado, e é isto que a cobrança segura numa reserva.
  */
  const [intent, setIntent] = useState (null)

  /*
    O passo do sinal só entra no fluxo quando o salão aceita pagamento adiantado — o que
    depende de ele ter conta ativa no Cutilei Pay. Por isso a régua de passos é montada a
    partir daqui, e não é fixa: `StepRow` e `StepLine` recebem o total para continuarem
    batendo com os círculos nos dois casos.
  */
  const steps = [
    {number: 0, label: 'Categorias'},
    {number: 1, label: 'Serviço'},
    {number: 2, label: 'Horário'},
    {number: 3, label: 'Profissional'},
    {number: 4, label: 'Confirmar'},
    ...onlinePayment?.enabled ? [{number: PAYMENT_STEP, label: 'Pagamento'}] : []
  ]

  const [currentStep, setCurrentStep] = useState (steps[0])
  const isPaymentStep = currentStep.number === PAYMENT_STEP
  const mapsAddress = business?.addresses[0] ? Address.formatAddressWithNoComplement (business?.addresses[0]) : ''
  const addressUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsAddress)}`

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      let scheduleData = schedule

      const [
        { data: business },
        { data: serviceCategories },
        { data: services },
        { data: onlinePayment }
      ] = await Promise.all ([
        cutileiApi.get (`/businesses/${business_id}`, { anonymous: true }),
        cutileiApi.get ('/service_categories', { anonymous: true }),
        cutileiApi.get (`/businesses/${business_id}/services?cutilei_services=false&accepts_online=true`, { anonymous: true }),
        /*
          O sinal é um extra: se a consulta falhar, o agendamento tem que continuar
          acontecendo sem ele. Dentro do `Promise.all` uma rejeição derrubaria as outras
          três e deixaria o link inteiro fora do ar — por isso a falha vira `enabled: false`
          aqui em vez de subir para o `catch`.
        */
        /*
          E enquanto o recurso não for lançado, a consulta nem acontece: em produção não há
          o que perguntar, e a resposta sintética mantém o passo fora do fluxo sem custar uma
          requisição. Ver `services/features.js`.
        */
        ONLINE_PAYMENT_ENABLED
          ? cutileiApi.get (`/businesses/${business_id}/online_payment`, { anonymous: true })
            .catch (error => {
              logApiError (error)
              return { data: {enabled: false} }
            })
          : Promise.resolve ({ data: {enabled: false} })
      ])

      if (schedule_id) {
        const { data } = await cutileiApi.get (`/schedules/${schedule_id}`, { anonymous: true })
        scheduleData = data
      }
      
      setSchedule ({...scheduleData, business, ...!!customer && {customer}})
      setBusiness (business)
      setServices (services)
      setServiceCategories (serviceCategories)
      setOnlinePayment (onlinePayment)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const handleNextStep = addedData => {
    setSchedule ({ ...schedule, ...addedData })
    nextStep ()
  }

  const handleScheduleOptionSelected = optionData => {
    const { date, scheduleOption, combinationOptions } = optionData

    if (scheduleOption) {
      setAlternativeSchedules ([{ ...schedule, ...scheduleOption, date }])
    } else if (combinationOptions) {
      setAlternativeSchedules ([
        { ...schedule, ...combinationOptions[0], date },
        { ...schedule, ...combinationOptions[1], date }
      ])
    }

    nextStep (2)
  }

  /*
    A confirmação termina de dois jeitos, e qual deles depende de o salão exigir sinal.

    Sem sinal, o agendamento já está marcado e o fluxo acaba aqui. Com sinal, o que chega é a
    intenção — cliente cadastrado e horários pretendidos —, e o agendamento só vai existir
    depois que o pagamento for confirmado.
  */
  const handleConfirmed = ({ schedules, customer, intended }) => {
    if (intended) {
      setIntent ({ customer, intended })
      return nextStep ()
    }

    setCreatedSchedules (schedules)
    goToSuccess (schedules)
  }

  /* Os agendamentos vêm da confirmação do pagamento: é ela que os cria. */
  const handlePaid = (payment, schedules) => goToSuccess (schedules ?? createdSchedules, payment)

  /*
    A reserva do horário venceu antes de o cliente pagar. Volta ao passo de escolher horário,
    que é o único lugar onde dá para recomeçar: o horário anterior já voltou para a praça.
  */
  const handleHoldExpired = () => {
    setIntent (null)
    setCurrentStep (steps[2])
  }

  const goToSuccess = (schedules, payment = null) => {
    // replace evita que o botão "voltar" do navegador remonte o formulário
    // preenchido e dispare um segundo cadastro do mesmo cliente.
    navigate ('/schedule/success', { state: { schedules, payment }, replace: true })
  }

  const nextStep = (step = 1) => setCurrentStep (steps[currentStep.number + step])
  const prevStep = (step = 1) => {
    setCurrentStep (steps[currentStep.number - step])
    setAlternativeSchedules ([])
  }

  return loading ? null : (
    <Container>
      <LogoRow>
        <SmallImage src={CutileiLogo} alt='Cutilei'/>
        <Title style={{margin: 0}}>Cutilei</Title>
      </LogoRow>
      <Row style={{alignItems: 'center'}}>
        <Column>
          <Row style={{position: 'relative', width: 'auto'}}>
            <Image src={business.logo}/>
            {/* {business?.average_rating > 0 && (
              <RatingContainer>
                <FA.FaStar size={14} color='#FFC100'/>
                <SubTitle>
                  {business?.average_rating}
                </SubTitle>
              </RatingContainer>
            )} */}
          </Row>
        </Column>
        <Column style={{alignItems: 'flex-start'}}>
          <Title>{business.name}</Title>
          <Block>
            <TextRow>
              <LinkText
                href={`https://api.whatsapp.com/send?phone=${business.cellphone.replace (/\D/g, '')}`}
                style={{color: '#44CF6C', fontWeight: 'bold'}}
              >
                <FA.FaWhatsapp size={18} color='#44CF6C'/>
                {Phone.formatPhone (business.cellphone, true)}
              </LinkText>
            </TextRow>
          </Block>
          {business.social_medias.map (socialMedia => (
            <Block key={socialMedia.id}>
              <LinkText href={socialMedia.link}>
                {socialMedia.name.toLowerCase () === 'instagram' ? (
                  <FA.FaInstagram size={18} color='#252525'/>
                ) : socialMedia.name.toLowerCase () === 'tiktok' ? (
                  <FA.FaTiktok size={18} color='#000000'/>
                ) : (
                  <FA.FaFacebook size={18} color='#0866FF'/>
                )}
                <SubTitle>{socialMedia.name}</SubTitle>
              </LinkText>
            </Block>
          ))}
          <Block>
            <LinkText href={addressUrl} target='_blank' rel='noopener noreferrer'>
              <IoLocationSharp size={20} color='#FF3939' style={{marginLeft: -1, marginRight: -2}}/>
              <SubTitle>Como chegar</SubTitle>
            </LinkText>
          </Block>
        </Column>
      </Row>
      <StepRow stepCount={steps.length}>
        {steps.map ((step, index) => (
          <StepContainer key={index}>
            <StepCircle step={step} currentStep={currentStep}>
              {currentStep.number > step.number ? (
                <FA.FaCheck color='#FFFFFF' size={14}/>
              ) : (
                <InfoText>{step.number + 1}</InfoText>
              )}
            </StepCircle>
            <StepLabel>{step.label}</StepLabel>
          </StepContainer>
        ))}
        <StepLine currentStep={currentStep.number} stepCount={steps.length}/>
      </StepRow>
      {currentStep.number > 0 && !isPaymentStep && (
        <SmallButton onClick={() => prevStep (alternativeSchedules.length > 0 ? 2 : 1)}>
          <Circle>
            <FA.FaChevronLeft color='#FFFFFF' size={14} style={{marginLeft: -2}}/>
          </Circle>
          <SubTitle>Voltar</SubTitle>
        </SmallButton>
      )}
      {currentStep.number === 0 && (
        <CategoriesForm
          services={services}
          serviceCategories={serviceCategories}
          onProceed={handleNextStep}
        />
      )}
      {currentStep.number === 1 && (
        <ServicesForm
          schedule={schedule}
          services={services}
          onProceed={handleNextStep}
        />
      )}
      {currentStep.number === 2 && (
        <TimeTablesForm
          schedule={schedule}
          onProceed={handleNextStep}
          onScheduleOptionSelected={handleScheduleOptionSelected}
        />
      )}
      {currentStep.number === 3 && (
        <ProfessionalsForm
          schedule={schedule}
          onProceed={handleNextStep}
        />
      )}
      {currentStep.number === 4 && (
        <Confirmation
          schedule={schedule}
          alternativeSchedules={alternativeSchedules}
          requiresDeposit={!!onlinePayment?.enabled}
          onConfirm={handleConfirmed}
        />
      )}
      {isPaymentStep && (
        <Payment
          business={business}
          customer={intent?.customer}
          intended={intent?.intended ?? []}
          config={onlinePayment}
          onPaid={handlePaid}
          onExpired={handleHoldExpired}
        />
      )}
    </Container>
  )
}
